/**
 * Project Counter — Live-Spenden-Counter pro Projekt-Karte via Twingle-API.
 *
 * Frontend ruft `/api/twingle/{projectId}` an. Der Symfony-Controller
 * App\Controller\TwingleProxyController setzt den API-Key, ruft die echte
 * Twingle-API auf, cached 5 Minuten und liefert ein normalisiertes JSON:
 *   { current, donors, goal, percentage, donateUrl }
 *
 * Erwartetes Markup (rsce_projects / rsce_twinglecounter / rsce_projectstatus):
 *
 *   <div class="ds-project-card__counter is-loading"
 *        data-twingle-card
 *        data-twingle-project="24346">
 *     <span data-twingle-current>0 €</span>
 *     <span data-twingle-goal-label>von <span data-twingle-goal-value>0</span> €</span>
 *     <div data-twingle-progressbar><div data-twingle-progress-fill></div></div>
 *     <span data-twingle-percent>0 %</span>
 *     <span data-twingle-donors>0 Unterstützer:innen</span>
 *     <span data-twingle-empty>Sei die/der Erste!</span>
 *     <a data-twingle-donate href="#">Jetzt spenden</a>
 *   </div>
 *
 * Die Card startet mit `.is-loading` (Skeleton). render() entfernt die Klasse,
 * sobald Daten vorliegen — alle Elemente sind von Anfang an im DOM, damit kein
 * Layout-Shift (CLS) entsteht.
 */

import counterMessages from "../../data/counter-messages.json";

const CACHE_TTL_MS = 5 * 60 * 1000;
const STORAGE_PREFIX = "wsz_twingle_";

const PROXY_ENDPOINT = (projectId) =>
  `/api/twingle/${encodeURIComponent(projectId)}`;

const EUR = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
const NUM = new Intl.NumberFormat("de-DE");
const INT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });

// ──────────────────────── Spendenaufruf-Texte ────────────────────────
// Texte aus counter-messages.json, getrennt nach Zustand (empty / hasDonations).
// Pro Karte + Zustand wird EINMAL zufällig ein Template gewählt und gemerkt,
// damit der Text beim Re-Render (Cache → Fetch) nicht flackert. Platzhalter
// werden bei jedem Render mit aktuellen Werten gefüllt (Zahl kann sich ändern).
const pickedMsg = new WeakMap();

function pickMessage(card, key, data) {
  const pool = Array.isArray(counterMessages[key]) ? counterMessages[key] : [];
  if (pool.length === 0) return "";
  let store = pickedMsg.get(card);
  if (!store) {
    store = {};
    pickedMsg.set(card, store);
  }
  if (store[key] === undefined) {
    store[key] = pool[Math.floor(Math.random() * pool.length)];
  }
  return store[key]
    .replace(/\{donors\}/g, NUM.format(data.donors ?? 0))
    .replace(/\{current\}/g, EUR.format(data.current ?? 0));
}

// ──────────────────────────── Cache ────────────────────────────

function loadCache(projectId) {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + projectId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function saveCache(projectId, data) {
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + projectId,
      JSON.stringify({ ts: Date.now(), data }),
    );
  } catch (_) {
    /* private mode / quota — silent */
  }
}

// ──────────────────────────── Fetch ────────────────────────────

async function fetchTwingleStatus(projectId) {
  const res = await fetch(PROXY_ENDPOINT(projectId), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error("twingle http " + res.status);
  return res.json();
}

function pickNumeric(obj, keys) {
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "" && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
}

/**
 * Normalisiert die Proxy-Antwort auf {current, donors, goal, donateUrl}.
 * 0 ist ein gültiger Wert — wenn `current` vorhanden ist (auch als 0),
 * gilt die Antwort als „valide".
 */
function normaliseTwingleData(raw) {
  if (!raw || typeof raw !== "object") return null;
  const current = pickNumeric(raw, ["current", "amount", "donated", "total", "sum"]);
  const donors = pickNumeric(raw, ["donors", "donor_count", "donations", "count"]);
  const goal = pickNumeric(raw, ["goal", "target", "donation_goal", "target_amount"]);
  if (current === null) return null;
  return {
    current,
    donors: donors ?? 0,
    goal: goal ?? 0,
    donateUrl: typeof raw.donateUrl === "string" ? raw.donateUrl : "",
  };
}

// ──────────────────────────── Render ────────────────────────────

function render(card, data) {
  if (!data) return;

  const elCurrent = card.querySelector("[data-twingle-current]");
  const elFill = card.querySelector("[data-twingle-progress-fill]");
  const elBar = card.querySelector("[data-twingle-progressbar]");
  const elPercent = card.querySelector("[data-twingle-percent]");
  const elGoalLabel = card.querySelector("[data-twingle-goal-label]");
  const elGoalValue = card.querySelector("[data-twingle-goal-value]");
  const elDonors = card.querySelector("[data-twingle-donors]");
  const elDonorsText = card.querySelector("[data-twingle-donors-text]");
  const elEmpty = card.querySelector("[data-twingle-empty]");
  const elEmptyText = card.querySelector("[data-twingle-empty-text]");
  const elSep = card.querySelector(".ds-counter__sep");

  const { current, donors, goal } = data;

  // Skeleton beenden — ab jetzt echte Werte.
  card.classList.remove("is-loading");

  if (elCurrent) elCurrent.textContent = EUR.format(current);

  // Goal-abhängige Elemente: bei goal>0 zeigen, sonst kollabieren.
  const hasGoal = goal > 0;
  card.classList.toggle("ds-counter--nogoal", !hasGoal);
  if (hasGoal) {
    if (elGoalValue) elGoalValue.textContent = INT.format(goal);
    const percent = Math.min(100, Math.round((current / goal) * 100));
    if (elFill) {
      requestAnimationFrame(() => {
        elFill.style.width = percent + "%";
      });
    }
    if (elBar) elBar.setAttribute("aria-valuenow", String(percent));
    if (elPercent) elPercent.textContent = `${percent} %`;
  }
  [elGoalLabel, elBar, elPercent, elSep].forEach((el) => {
    if (el) el.toggleAttribute("hidden", !hasGoal);
  });

  // Spenden-Button-URL aus der API übernehmen (spart manuelle Pflege).
  if (data.donateUrl) {
    card.querySelectorAll("[data-twingle-donate]").forEach((el) => {
      el.href = data.donateUrl;
    });
  }

  const hasDonors = donors !== null && donors > 0;
  if (elDonors) {
    if (hasDonors) {
      // Variierter "schon dabei"-Text; {donors} als Fallback auf den reinen Count.
      // Text in den inneren Span schreiben, damit das Badge-Icon erhalten bleibt
      // (Fallback auf den Container, falls kein Text-Span vorhanden ist).
      const msg = pickMessage(card, "hasDonations", data);
      (elDonorsText || elDonors).textContent =
        msg || `${NUM.format(donors)} Unterstützer:innen`;
    }
    elDonors.toggleAttribute("hidden", !hasDonors);
  }
  if (!hasDonors && elEmptyText) {
    // Variierter "sei der/die Erste"-Aufruf (Icon im Eltern-Span bleibt erhalten).
    const msg = pickMessage(card, "empty", data);
    if (msg) elEmptyText.textContent = msg;
  }
  if (elEmpty) elEmpty.toggleAttribute("hidden", hasDonors);
}

// ──────────────────────────── Per-Card-Init ────────────────────────────

async function initCard(card) {
  const projectId = card.dataset.twingleProject;
  if (!projectId) {
    card.classList.remove("is-loading");
    return;
  }

  // Sofort aus dem Cache rendern (synchron, vor dem Paint) — kein Skeleton-Flash
  // beim Wiederbesuch.
  const cached = loadCache(projectId);
  if (cached) render(card, cached);

  try {
    const raw = await fetchTwingleStatus(projectId);
    const data = normaliseTwingleData(raw);
    if (data) {
      saveCache(projectId, data);
      render(card, data);
    } else if (!cached) {
      card.classList.remove("is-loading");
    }
  } catch (err) {
    if (!cached) {
      card.classList.remove("is-loading");
      console.warn("project-counter: twingle fetch failed", projectId, err);
    }
  }
}

// ──────────────────────────── Init ────────────────────────────

export function init() {
  document
    .querySelectorAll("[data-twingle-card]")
    .forEach((card) => initCard(card));
}
